import { useState, useEffect } from "react";

interface IProps {
	ref: HTMLElement | null;
	forceRerender?: number;
	trackOnResize?: boolean;
	skipTrackOnMutate?: boolean;
}

const useTrackHeight = ({ ref, forceRerender, trackOnResize, skipTrackOnMutate = false }: IProps): { height: number } => {
	const [height, setHeight] = useState(0);

	useEffect(() => {
		// create a mutation observer to track the height of the element
		if (ref) {
			// initialize on load:
			setHeight(ref.getBoundingClientRect()?.height || 0);

			// listen for changes:
			const observer = new MutationObserver(() => {
				setHeight(ref.getBoundingClientRect()?.height || 0);
			});

			if (!skipTrackOnMutate) {
				observer.observe(ref, {
					attributes: true,
					characterData: true,
					childList: true,
					subtree: true,
				});
			}

			// listen for resize:
			const resizeObserver = new ResizeObserver((entries) => {
				const [entry] = entries;
				// no need to call getBoundingClientRect here which forces a repaint because
				// the target element's resized height is already a part of the resize observer entries
				const borderBoxHeight = entry?.borderBoxSize?.[0]?.blockSize;
				setHeight(borderBoxHeight ?? entry?.contentRect?.height ?? 0);
			});

			if (trackOnResize) {
				resizeObserver.observe(ref);
			}

			return () => {
				observer.disconnect();
				resizeObserver.disconnect();
			};
		}
	}, [ref, forceRerender, trackOnResize, skipTrackOnMutate]);

	return {
		height
	};
};

export default useTrackHeight;
